import { z } from "zod"
import type { AgentExecutionSettings } from "./agent-execution"
import { runInputIdentitySchema, runStatusSummarySchema } from "./schemas/run-status"

export const PREPARATION_TASK_STATUS = {
  queued: "queued",
  running: "running",
  blocked: "blocked",
  stopped: "stopped",
  ready: "ready",
  failed: "failed",
  stale: "stale",
} as const
export type PreparationTaskStatus =
  (typeof PREPARATION_TASK_STATUS)[keyof typeof PREPARATION_TASK_STATUS]
export const PREPARATION_TOOL = {
  inspect: "inspect",
  extract: "extract",
  copy: "copy",
  values: "values",
  validate: "validate",
  build: "build",
  finish: "finish",
  block: "block",
} as const
export type PreparationTool = (typeof PREPARATION_TOOL)[keyof typeof PREPARATION_TOOL]
export const preparationToolArgumentsSchema = z
  .object({ reason: z.string().min(1).max(1000) })
  .strict()
export const PREPARATION_PROMPT_VERSION = "listing-preparation-v4"
export const PREPARATION_TOOL_VERSION = "listing-preparation-tools-v2"
export const PREPARATION_LIMITS = {
  maxModelCalls: 32,
  maxTokens: 100_000,
  maxToolCalls: 32,
  maxDurationMs: 60 * 60_000,
} as const
export type PreparationLimits = {
  maxModelCalls: number
  maxTokens: number
  maxToolCalls: number
  maxDurationMs: number
}
export type PreparationUsage = {
  modelCalls: number
  inputTokens: number
  outputTokens: number
  reservedTokens: number
}
export type PreparationMessage = {
  id: string
  role: "user" | "assistant" | "tool"
  content: string
  createdAt: string
}
export const preparationArtifactSchema = z.object({
  id: z.number().int(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  filename: z.string(),
  rowCount: z.number().int().nonnegative(),
  columnCount: z.number().int().nonnegative(),
  excludedCount: z.number().int().nonnegative(),
  gapCount: z.number().int().nonnegative(),
  inputHash: z.string(),
  exclusions: z.array(z.object({ configSku: z.string(), reason: z.string() })),
  qualityChecks: z.array(z.object({ name: z.string(), passed: z.boolean(), detail: z.string() })),
})

const canonicalStatusSchema = runStatusSummarySchema.pick({
  readiness: true,
  products: true,
  feed: true,
  coverage: true,
})
const contentEvidenceSchema = z
  .object({
    fingerprint: z.string(),
    evidenceId: z.string().optional(),
    workId: z.string().optional(),
  })
  .strict()
const verifiedArtifactEvidenceSchema = z
  .object({
    artifact: preparationArtifactSchema,
    canonicalStatus: canonicalStatusSchema,
  })
  .strict()
const errorEvidenceSchema = z
  .object({
    kind: z.enum([
      "validation",
      "not_found",
      "expired",
      "conflict",
      "illegal_transition",
      "unauthorized",
      "forbidden",
      "upstream",
      "unavailable",
      "network",
      "aborted",
      "unknown",
    ]),
    message: z.string().min(1),
  })
  .strict()
function toolEvidence<Tool extends PreparationTool, Data extends z.ZodType>(
  tool: Tool,
  data: Data
) {
  return z.discriminatedUnion("outcome", [
    z.object({ tool: z.literal(tool), outcome: z.literal("completed"), data }).strict(),
    z
      .object({ tool: z.literal(tool), outcome: z.literal("failed"), error: errorEvidenceSchema })
      .strict(),
  ])
}
function contentToolEvidence<Tool extends "extract" | "copy" | "values">(tool: Tool) {
  return z.discriminatedUnion("outcome", [
    ...toolEvidence(tool, contentEvidenceSchema).options,
    z
      .object({ tool: z.literal(tool), outcome: z.literal("pending"), data: contentEvidenceSchema })
      .strict(),
  ])
}
/** Historical operation results. Readiness must always use the current task projection. */
export const preparationEvidenceSchema = z.discriminatedUnion("tool", [
  toolEvidence(
    "inspect",
    z
      .object({
        sourceFingerprint: z.string(),
        extractionNeeded: z.array(z.string()),
        sourceErrors: z.array(z.object({ configSku: z.string(), message: z.string() }).strict()),
        copyGaps: z.array(z.object({ configSku: z.string(), role: z.string() }).strict()),
        canonicalStatus: canonicalStatusSchema,
      })
      .strict()
  ),
  contentToolEvidence("extract"),
  contentToolEvidence("copy"),
  z.discriminatedUnion("outcome", [
    ...toolEvidence("values", contentEvidenceSchema).options,
    z
      .object({
        tool: z.literal("values"),
        outcome: z.literal("pending"),
        data: z.object({ waiting: z.string() }).strict(),
      })
      .strict(),
  ]),
  toolEvidence(
    "validate",
    z
      .object({
        inputIdentity: runInputIdentitySchema,
        gapCount: z.number().int().nonnegative(),
        buildBlocked: z.boolean(),
      })
      .strict()
  ),
  toolEvidence(
    "build",
    z.discriminatedUnion("kind", [
      z
        .object({
          kind: z.literal("built"),
          feedArtifactId: z.number().int().positive(),
          sha256: z.string().regex(/^[a-f0-9]{64}$/),
          rowCount: z.number().int().nonnegative(),
          columnCount: z.number().int().nonnegative(),
          inputIdentity: runInputIdentitySchema,
        })
        .strict(),
      verifiedArtifactEvidenceSchema.extend({ kind: z.literal("verified") }),
    ])
  ),
  toolEvidence("finish", verifiedArtifactEvidenceSchema),
  toolEvidence("block", z.object({ reason: z.string().min(1) }).strict()),
])
export type PreparationEvidence = z.infer<typeof preparationEvidenceSchema>

export type PreparationStep = {
  id: string
  tool: PreparationTool
  argumentsHash?: string | undefined
  modelUsed?: string | undefined
  status: "intent" | "completed" | "failed"
  result: PreparationEvidence | null
  acceptedEvidence?: { configSku: string; evidenceId: string; sourceHash: string }[] | undefined
  createdAt: string
}
export type PreparationArtifact = {
  id: number
  sha256: string
  filename: string
  rowCount: number
  columnCount: number
  excludedCount: number
  gapCount: number
  inputHash: string
  exclusions: { configSku: string; reason: string }[]
  qualityChecks: { name: string; passed: boolean; detail: string }[]
}
export type PreparationTask = {
  id: string
  runId: number
  runSlug: string
  marketplace: string
  category: string
  configSkus: string[]
  status: PreparationTaskStatus
  createdAt: string
  updatedAt: string
  blocker: string | null
  messages: PreparationMessage[]
  steps: PreparationStep[]
  usage: PreparationUsage
  limits: PreparationLimits
  artifact: PreparationArtifact | null
  model: string
  promptVersion: string
  execution?: AgentExecutionSettings | undefined
  modelReservations?: { id: string; tokens: number; settled: boolean }[] | undefined
}
export type PreparationSnapshot = {
  scope: {
    runId: number
    runSlug: string
    marketplace: string
    category: string
    configSkus: string[]
  }
  task: PreparationTask | null
  admissionEnabled: boolean
  admissionBlocker: string | null
}
